import React from "react";
import { clsx } from "clsx";

export interface BottomNavItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    icon: React.ReactNode;
    label: string;
    /** Whether this tab represents the current route. */
    active?: boolean;
}

/**
 * A single tab in a bottom navigation bar. Presentational only — the consuming app
 * decides `active` (e.g. from `useLocation`) and navigates in `onClick` (e.g. `useNavigate`).
 */
export const BottomNavItem: React.FC<BottomNavItemProps> = ({
    icon,
    label,
    active = false,
    className,
    ...props
}) => {
    return (
        <button
            type="button"
            aria-current={active ? "page" : undefined}
            className={clsx(
                "relative flex flex-1 flex-col items-center gap-1 py-2 text-[10px] font-bold tracking-wider uppercase transition-all duration-200",
                active
                    ? "text-primary dark:text-gold-light"
                    : "text-neutral-secondary hover:text-neutral-dark dark:hover:text-slate-300",
                className,
            )}
            {...props}
        >
            <span
                className={clsx(
                    "flex h-7 w-7 items-center justify-center rounded-xl transition-all duration-300",
                    active && "bg-gold/15 text-gold-dark dark:text-gold-light",
                )}
            >
                {icon}
            </span>
            <span className="font-mono text-[10px] transition-opacity duration-200">{label}</span>
            {active && (
                <span className="absolute bottom-0.5 left-1/2 h-[3px] w-6 -translate-x-1/2 rounded-full bg-gold" />
            )}
        </button>
    );
};
